import * as React from 'react';

import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { useUser } from '@/hooks/v2/use-user';
import { hasAnyAccountRoles, hasCommunityRolesV2 } from '@/utils/has-roles';

import type { HelpArticle } from './data/help-articles';

/**
 * Shared role gating for help content. Mirrors the audience rules used on the
 * help center so the help page, the contextual help dialog, and the top-bar
 * entry point all agree on what a given user can see.
 */
export function useHelpAccess(): { canSeeArticle: (article: HelpArticle) => boolean } {
  const { account, community } = useUser();

  const isCommunityManager = hasCommunityRolesV2(community, [COMMUNITY_ROLES.COMMUNITY_MANAGER]);
  const isBoardMember = hasCommunityRolesV2(community, [COMMUNITY_ROLES.BOARD_MEMBER]);
  const isAccountUser = account
    ? hasAnyAccountRoles(account, [ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER, ACCOUNT_ROLES.ACCOUNT_OWNER])
    : false;
  const canManageCommunity = isCommunityManager || isAccountUser;
  const canSeeBoardHelp = isBoardMember || canManageCommunity;

  const canSeeArticle = React.useCallback(
    (article: HelpArticle): boolean => {
      if (article.feature === 'discussion' && community?.discussion_page_enabled === false) return false;
      if (article.audience === 'board') return canSeeBoardHelp;
      if (article.audience === 'community-manager') return canManageCommunity;
      if (article.audience === 'account') return isAccountUser;
      return true;
    },
    [community?.discussion_page_enabled, canSeeBoardHelp, canManageCommunity, isAccountUser]
  );

  return { canSeeArticle };
}
